import { X } from 'lucide-react';
import { type ComponentType, useMemo, useState } from 'react';
import { type Control, useController } from 'react-hook-form';
import type { GroupBase, SingleValue, StylesConfig } from 'react-select';
import type { CreatableProps } from 'react-select/creatable';
import CreatableSelect from 'react-select/creatable';

import { useCreateTag, useIntl, useTagAutocomplete } from '../../hooks';
import type { Tag } from '../../types/tag.js';
import type { UserFormValues } from '../../validations/user-form.js';
import { Badge } from '../ui';

const MAX_USER_TAGS = 50;

type UserTagsFieldProps = {
  control: Control<UserFormValues>;
  disabled?: boolean;
};

interface TagOption {
  value: string;
  label: string;
  tagId?: string;
}

type CreatableSelectProps = CreatableProps<TagOption, false, GroupBase<TagOption>>;

const CreatableSelectComponent = CreatableSelect as unknown as ComponentType<CreatableSelectProps>;

const selectStyles: StylesConfig<TagOption, false, GroupBase<TagOption>> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 6,
    borderColor: 'var(--color-border)',
    color: 'var(--color-foreground)',
    ':hover': {
      borderColor: 'var(--color-border)',
      cursor: 'pointer',
    },
    backgroundColor: state.isDisabled ? 'var(--color-background-dark)' : 'var(--color-input)',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 30,
    backgroundColor: 'var(--color-background-light)',
    border: '1px solid var(--color-border)',
  }),
  option: (provided) => ({
    ...provided,
    backgroundColor: 'var(--color-background-light)',
    color: 'var(--color-foreground)',
    ':hover': {
      backgroundColor: 'var(--color-background-dark)',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'var(--color-foreground)',
  }),
};

export function UserTagsField({ control, disabled = false }: UserTagsFieldProps) {
  const intl = useIntl();
  const {
    field: { value: formValue = [], onChange },
  } = useController({ name: 'tags', control });

  const tags: Tag[] = useMemo(() => (Array.isArray(formValue) ? formValue : []), [formValue]);
  const [inputValue, setInputValue] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const assignedNames = useMemo(() => {
    return new Set(tags.map((tag) => tag.name.toLowerCase()));
  }, [tags]);

  const assignedIds = useMemo(() => new Set(tags.map((tag) => tag.id)), [tags]);

  const suggestionsQuery = useTagAutocomplete(inputValue.trim(), 10, true);

  const availableOptions: TagOption[] = useMemo(() => {
    const data = suggestionsQuery.data ?? [];
    return data
      .filter((tag) => !assignedNames.has(tag.name.toLowerCase()))
      .map((tag) => ({ value: tag.name, label: tag.name, tagId: tag.id }));
  }, [assignedNames, suggestionsQuery.data]);

  const maxReached = tags.length >= MAX_USER_TAGS;

  const createTag = useCreateTag();

  const resetInput = () => {
    setInputValue('');
  };

  const handleInputChange: NonNullable<CreatableSelectProps['onInputChange']> = (
    nextValue,
    action,
  ) => {
    if (action.action === 'input-change') {
      setInputValue(nextValue);
    } else if (action.action === 'menu-close') {
      resetInput();
    }
  };

  const handleAddTag = (tag: Tag) => {
    if (assignedIds.has(tag.id)) {
      setMessage(intl.formatMessage({ id: 'userTags.alreadyAssigned' }, { name: tag.name }));
      resetInput();
      return;
    }
    onChange([...tags, tag]);
    setMessage(null);
    resetInput();
  };

  const handleSelect = (option: SingleValue<TagOption>) => {
    if (!option) return;
    if (option.tagId) {
      const suggestion = suggestionsQuery.data?.find((tag) => tag.id === option.tagId);
      if (suggestion) {
        handleAddTag(suggestion);
      } else {
        handleAddTag({ id: option.tagId, name: option.label });
      }
      return;
    }
    const normalized = option.value.toLowerCase();
    const suggestion = suggestionsQuery.data?.find((tag) => tag.name.toLowerCase() === normalized);
    if (suggestion) {
      handleAddTag(suggestion);
    }
  };

  const handleCreate = async (value: string) => {
    const name = value.trim();
    if (!name) return;
    if (maxReached) {
      setMessage(intl.formatMessage({ id: 'userTags.maxReached' }, { max: MAX_USER_TAGS }));
      resetInput();
      return;
    }
    if (name.length > 64) {
      setMessage(intl.formatMessage({ id: 'userTags.maxLength' }));
      resetInput();
      return;
    }
    const normalized = name.toLowerCase();
    if (assignedNames.has(normalized)) {
      setMessage(intl.formatMessage({ id: 'userTags.alreadyAssigned' }, { name }));
      resetInput();
      return;
    }
    const suggestion = suggestionsQuery.data?.find((tag) => tag.name.toLowerCase() === normalized);
    if (suggestion) {
      handleAddTag(suggestion);
      return;
    }
    try {
      const created = await createTag.mutateAsync({ name });
      handleAddTag(created);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : intl.formatMessage({ id: 'userTags.createError' });
      setMessage(msg);
    }
  };

  const handleRemove = (tagId: string) => {
    const next = tags.filter((tag) => tag.id !== tagId);
    onChange(next);
    setMessage(null);
  };

  return (
    <div className="space-y-3">
      {tags.length === 0 ? (
        <div className="text-sm text-slate-500">
          {intl.formatMessage({ id: 'userTags.noTags' })}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Badge
              key={tag.id}
              variant="secondary"
              className="flex items-center gap-1 pr-1 text-xs"
            >
              <span>{tag.name}</span>
              <button
                type="button"
                className="inline-flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-slate-200"
                aria-label={intl.formatMessage({ id: 'userTags.removeTag' }, { name: tag.name })}
                onClick={() => handleRemove(tag.id)}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <CreatableSelectComponent
        inputValue={inputValue}
        onInputChange={handleInputChange}
        value={null}
        onChange={handleSelect}
        onCreateOption={(value) => {
          void handleCreate(value);
        }}
        isClearable
        classNamePrefix="user-tags-select"
        placeholder={intl.formatMessage({ id: 'userTags.placeholder' })}
        isDisabled={disabled || maxReached || createTag.isPending}
        isLoading={suggestionsQuery.isFetching || createTag.isPending}
        options={availableOptions}
        formatCreateLabel={(value: string) =>
          intl.formatMessage({ id: 'userTags.createLabel' }, { value })
        }
        components={{ IndicatorSeparator: null }}
        styles={selectStyles}
        inputId="user-tags-select"
        instanceId="user-tags-select"
      />

      {message ? <div className="text-sm text-red-600">{message}</div> : null}
      {maxReached ? (
        <div className="text-xs text-slate-500">
          {intl.formatMessage({ id: 'userTags.maxReached' }, { max: MAX_USER_TAGS })}
        </div>
      ) : null}
    </div>
  );
}
